import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const TechnicianDetailsModal = ({ isOpen, onClose, technician, onTechnicianUpdated }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data when technician changes
  useEffect(() => {
    if (technician) {
      setFormData({
        firstName: technician.first_name || technician.name?.split(' ')[0] || '',
        lastName: technician.last_name || technician.name?.split(' ')[1] || '',
        phone: technician.phone || '',
        email: technician.email || '',
        status: technician.status || 'active'
      });
    }
  }, [technician]);

  // Early return after all hooks
  if (!isOpen || !technician) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success text-success-foreground';
      case 'inactive':
        return 'bg-muted text-muted-foreground';
      case 'suspended':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'inactive':
        return 'Inaktiv';
      case 'suspended':
        return 'Suspendiert';
      default:
        return 'Unbekannt';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  const statusOptions = [
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
    { value: 'suspended', label: 'Suspendiert' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'Vorname ist erforderlich';
    }
    
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Nachname ist erforderlich';
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const updateData = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone: formData.phone.trim() || null,
        email: formData.email.trim().toLowerCase(),
        status: formData.status
      };


      // Call the API to update the technician
      const { data: updatedTechnician, error } = await supabase
        .from('technicians')
        .update(updateData)
        .eq('id', technician.id)
        .select()
        .single();

      if (error) {
        throw error;
      }
      
      
      // Exit edit mode
      setIsEditMode(false);
      
      // Notify parent component
      if (onTechnicianUpdated) {
        onTechnicianUpdated({
          ...technician,
          ...updatedTechnician,
          name: `${updatedTechnician.first_name} ${updatedTechnician.last_name}`
        });
      }
      
      // Show success message
      alert('Techniker erfolgreich aktualisiert!');
      
    } catch (error) {
      console.error('Error updating technician:', error);
      
      // Handle specific errors
      if (error.code === '23505' && error.message.includes('email')) {
        setErrors({ email: 'Diese E-Mail-Adresse wird bereits verwendet' });
      } else {
        setErrors({ general: `Fehler beim Aktualisieren: ${error.message}` });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (technician) {
      setFormData({
        firstName: technician.first_name || technician.name?.split(' ')[0] || '',
        lastName: technician.last_name || technician.name?.split(' ')[1] || '',
        phone: technician.phone || '',
        email: technician.email || '',
        status: technician.status || 'active'
      });
    }
    setErrors({});
    setIsEditMode(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="User" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Techniker Details
              </h2>
              <p className="text-text-secondary">
                Vollständige Informationen über {technician.name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

                 {/* Content */}
         <div className="p-6 space-y-6">
           {/* Error Display */}
           {errors.general && (
             <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
               <p className="text-red-700 text-sm">{errors.general}</p>
             </div>
           )}

           {/* Basic Information */}
           <div className="space-y-4">
             <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
               Persönliche Informationen
             </h3>
             
             {isEditMode ? (
               // Edit Form
               <div className="space-y-4">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <Input
                     label="Vorname *"
                     placeholder="Vorname eingeben"
                     value={formData.firstName}
                     onChange={(e) => handleInputChange('firstName', e.target.value)}
                     error={errors.firstName}
                     required
                   />
                   
                   <Input
                     label="Nachname *"
                     placeholder="Nachname eingeben"
                     value={formData.lastName}
                     onChange={(e) => handleInputChange('lastName', e.target.value)}
                     error={errors.lastName}
                     required
                   />
                 </div>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <Input
                     label="E-Mail *"
                     type="email"
                     placeholder="E-Mail-Adresse eingeben"
                     value={formData.email}
                     onChange={(e) => handleInputChange('email', e.target.value)}
                     error={errors.email}
                     required
                   />
                   
                   <Input
                     label="Telefonnummer"
                     placeholder="Telefonnummer eingeben (optional)"
                     value={formData.phone}
                     onChange={(e) => handleInputChange('phone', e.target.value)}
                     error={errors.phone}
                   />
                 </div>
                 
                 <div>
                   <Select
                     label="Status"
                     placeholder="Status auswählen"
                     options={statusOptions}
                     value={formData.status}
                     onChange={(value) => handleInputChange('status', value)}
                     error={errors.status}
                   />
                 </div>
               </div>
             ) : (
               // Read-only View
               <div className="space-y-3">
                 <div>
                   <label className="text-sm font-medium text-text-secondary">Vollständiger Name</label>
                   <p className="text-foreground">{technician.name}</p>
                 </div>
                 
                 <div>
                   <label className="text-sm font-medium text-text-secondary">E-Mail</label>
                   <p className="text-foreground">{technician.email || 'N/A'}</p>
                 </div>
                 
                 <div>
                   <label className="text-sm font-medium text-text-secondary">Telefonnummer</label>
                   <p className="text-foreground">{technician.phone || 'N/A'}</p>
                 </div>
                 
                 <div>
                   <label className="text-sm font-medium text-text-secondary">Status</label>
                   <div className="flex items-center space-x-2 mt-1">
                     <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(technician.status)}`}>
                       {getStatusText(technician.status)}
                     </span>
                   </div>
                 </div>
                 
                 <div>
                   <label className="text-sm font-medium text-text-secondary">Rolle</label>
                   <p className="text-foreground">{technician.role || 'Techniker'}</p>
                 </div>
               </div>
             )}
           </div>

          {/* Skills Section */}
          {technician.skills && technician.skills.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                Spezialisierungen
              </h3>
              <div className="flex flex-wrap gap-2">
                {technician.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full border border-primary/20"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Performance Metrics */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
              Leistungsmetriken
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-primary">{technician.currentOrders || 0}</div>
                <div className="text-sm text-text-secondary">Aktuelle Aufträge</div>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-success">{technician.completedToday || 0}</div>
                <div className="text-sm text-text-secondary">Heute erledigt</div>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-warning">
                  {technician.currentOrders > 0 ? 'Beschäftigt' : 'Verfügbar'}
                </div>
                <div className="text-sm text-text-secondary">Status</div>
              </div>
            </div>
          </div>

        </div>

                 {/* Footer */}
         <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
           {isEditMode ? (
             // Edit Mode Buttons
             <>
               <Button
                 variant="outline"
                 onClick={handleCancel}
                 disabled={loading}
               >
                 Abbrechen
               </Button>
               <Button
                 variant="default"
                 onClick={handleSave}
                 disabled={loading}
                 className="min-w-[100px]"
               >
                 {loading ? (
                   <>
                     <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                     Wird gespeichert...
                   </>
                 ) : (
                   <>
                     <Icon name="Save" size={16} className="mr-2" />
                     Speichern
                   </>
                 )}
               </Button>
             </>
           ) : (
             // View Mode Buttons
             <>
               <Button
                 variant="outline"
                 onClick={onClose}
               >
                 Schließen
               </Button>
               <Button
                 variant="default"
                 onClick={() => setIsEditMode(true)}
               >
                 <Icon name="Edit" size={16} className="mr-2" />
                 Bearbeiten
               </Button>
             </>
           )}
         </div>
      </div>
    </div>
  );
};

export default TechnicianDetailsModal;
